import { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
	fetchCronRuns,
	type CronRunRow,
	type CronRunsResponse,
	type CronJobSummary,
	type CronRunStatus
} from '@/lib/api';
import { HealthPageShell, formatRelative } from '../_shared/HealthPageShell';

function statusColor(status: CronRunStatus): 'success' | 'warning' | 'error' | 'default' {
	switch (status) {
		case 'success':
			return 'success';
		case 'error':
			return 'error';
		case 'running':
			return 'warning';
		default:
			return 'default';
	}
}

function formatDuration(ms: number | null): string {
	if (ms == null) return '—';
	if (ms < 1000) return `${ms}ms`;
	return `${(ms / 1000).toFixed(2)}s`;
}

function formatTimestamp(iso: string): string {
	return new Date(iso).toLocaleString();
}

function JobCard({ job }: { job: CronJobSummary }) {
	const { lastRun } = job;
	const total = job.successCount7d + job.errorCount7d;
	const successRate = total === 0 ? null : Math.round((job.successCount7d / total) * 100);

	return (
		<Paper className="p-5">
			<div className="flex items-start justify-between gap-3 mb-3">
				<div className="min-w-0">
					<Typography className="font-semibold font-mono">{job.name}</Typography>
					<Typography variant="caption" color="text.secondary">
						{lastRun
							? `last run ${formatRelative(lastRun.started_at)}`
							: 'no runs recorded yet'}
					</Typography>
				</div>
				{lastRun && (
					<Chip
						size="small"
						label={lastRun.status}
						color={statusColor(lastRun.status)}
						variant={lastRun.status === 'success' ? 'outlined' : 'filled'}
					/>
				)}
			</div>

			<div className="grid grid-cols-3 gap-3">
				<div>
					<Typography variant="caption" color="text.secondary" className="uppercase tracking-wide">
						Success 7d
					</Typography>
					<Typography className="text-xl font-bold text-emerald-600">
						{job.successCount7d}
					</Typography>
				</div>
				<div>
					<Typography variant="caption" color="text.secondary" className="uppercase tracking-wide">
						Errors 7d
					</Typography>
					<Typography
						className={`text-xl font-bold ${job.errorCount7d > 0 ? 'text-rose-600' : ''}`}
					>
						{job.errorCount7d}
					</Typography>
				</div>
				<div>
					<Typography variant="caption" color="text.secondary" className="uppercase tracking-wide">
						Avg duration
					</Typography>
					<Typography className="text-xl font-bold">
						{formatDuration(job.avgDurationMs7d)}
					</Typography>
				</div>
			</div>

			{successRate !== null && successRate < 100 && (
				<Typography variant="caption" color="text.secondary" className="mt-2 block">
					{successRate}% success rate over the last 7 days
				</Typography>
			)}
		</Paper>
	);
}

function RunRow({ run }: { run: CronRunRow }) {
	const [expanded, setExpanded] = useState(false);
	const hasDetails = Object.keys(run.details).length > 0;
	const hasError = !!run.error_message;
	const expandable = hasDetails || hasError;

	return (
		<>
			<tr
				className={`border-t align-top ${expandable ? 'cursor-pointer hover:bg-slate-50' : ''}`}
				onClick={() => expandable && setExpanded((v) => !v)}
			>
				<td className="py-2 pr-3 w-8">
					{expandable && (
						<IconButton
							size="small"
							sx={{
								transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
								transition: 'transform 120ms ease'
							}}
						>
							<ChevronRightIcon fontSize="small" />
						</IconButton>
					)}
				</td>
				<td className="py-2 pr-3 whitespace-nowrap text-xs">
					{formatTimestamp(run.started_at)}
				</td>
				<td className="py-2 pr-3 font-mono text-xs">{run.job_name}</td>
				<td className="py-2 pr-3">
					<Chip
						size="small"
						label={run.status}
						color={statusColor(run.status)}
						variant={run.status === 'success' ? 'outlined' : 'filled'}
					/>
				</td>
				<td className="py-2 pr-3 text-right whitespace-nowrap text-xs">
					{formatDuration(run.duration_ms)}
				</td>
				<td className="py-2 text-xs text-slate-500 truncate max-w-md">
					{run.error_message ?? summarizeDetails(run.details)}
				</td>
			</tr>
			{expandable && (
				<tr>
					<td colSpan={6} className="p-0">
						<Collapse in={expanded} unmountOnExit>
							<div className="bg-slate-50 border-t p-4 space-y-3">
								{hasError && (
									<div>
										<Typography variant="caption" color="text.secondary" className="uppercase tracking-wide">
											Error
										</Typography>
										<pre className="mt-1 text-xs font-mono text-rose-700 whitespace-pre-wrap">
											{run.error_message}
										</pre>
									</div>
								)}
								{hasDetails && (
									<div>
										<Typography variant="caption" color="text.secondary" className="uppercase tracking-wide">
											Details
										</Typography>
										<pre className="mt-1 text-xs font-mono bg-white rounded border p-3 overflow-x-auto">
											{JSON.stringify(run.details, null, 2)}
										</pre>
									</div>
								)}
								<Typography variant="caption" color="text.secondary" className="block font-mono">
									id: {run.id}
								</Typography>
							</div>
						</Collapse>
					</td>
				</tr>
			)}
		</>
	);
}

function summarizeDetails(details: Record<string, unknown>): string {
	const entries = Object.entries(details).filter(
		([, v]) => typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean'
	);
	if (entries.length === 0) return '';
	return entries.map(([k, v]) => `${k}: ${String(v)}`).join(' · ');
}

function CronHealthView() {
	const [data, setData] = useState<CronRunsResponse | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	function load() {
		setLoading(true);
		setError(null);
		fetchCronRuns()
			.then(setData)
			.catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
			.finally(() => setLoading(false));
	}

	useEffect(() => {
		load();
	}, []);

	return (
		<HealthPageShell
			title="Cron Jobs"
			subtitle={
				data?.fetchedAt
					? `Scheduled task execution log · updated ${formatRelative(data.fetchedAt)}`
					: 'Scheduled task execution log'
			}
			onRefresh={load}
			refreshDisabled={loading}
		>
			{error && <Alert severity="error">Failed to load cron runs: {error}</Alert>}

			{data && (
				<div className="flex flex-col gap-5">
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
						{data.jobs.map((job) => (
							<JobCard key={job.name} job={job} />
						))}
					</div>

					<Paper className="p-5">
						<Typography className="font-semibold mb-3">
							Recent runs
						</Typography>
						{data.recent.length === 0 ? (
							<Typography variant="body2" color="text.secondary">
								No runs recorded yet. New invocations will appear here.
							</Typography>
						) : (
							<div className="overflow-x-auto">
								<table className="w-full text-sm">
									<thead>
										<tr className="text-left text-slate-500">
											<th className="pb-2 w-8" />
											<th className="pb-2 pr-3">Started</th>
											<th className="pb-2 pr-3">Job</th>
											<th className="pb-2 pr-3">Status</th>
											<th className="pb-2 pr-3 text-right">Duration</th>
											<th className="pb-2">Summary</th>
										</tr>
									</thead>
									<tbody>
										{data.recent.map((run) => (
											<RunRow key={run.id} run={run} />
										))}
									</tbody>
								</table>
							</div>
						)}
					</Paper>
				</div>
			)}
		</HealthPageShell>
	);
}

export default CronHealthView;
